import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft, User, Mail, Phone, MapPin, Lock, Eye, EyeOff,
  Save, AlertCircle, CheckCircle, Trash2, Loader, Camera
} from 'lucide-react';

const API_BASE = 'https://hive-backend-4gcf.onrender.com';

function authHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

export default function ProfileSettings() {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileLoading, setProfileLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [profilePicture, setProfilePicture] = useState('');
  const [uploadingPic, setUploadingPic] = useState(false);
  const fileInputRef = useRef(null);

  const isLoggedIn = !!localStorage.getItem('token');

  const [profileData, setProfileData] = useState({
    name: '', email: '', phone: '', bio: '', location: '',
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '', newPassword: '', confirmPassword: '',
  });

  useEffect(() => {
    if (!isLoggedIn) { setProfileLoading(false); return; }
    const fetchProfile = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/auth/profile`, { headers: authHeaders() });
        const data = await res.json();
        if (res.ok) {
          setProfileData({
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            bio: data.bio || '',
            location: data.location || '',
          });
          setProfilePicture(data.profilePicture || '');
        }
      } catch (err) {
        console.error(err);
      } finally {
        setProfileLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const handleProfilePicUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { showMsg('error', 'Image must be under 5MB'); return; }

    setUploadingPic(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('image', file);

      const res = await fetch(`${API_BASE}/api/upload/profile`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        setProfilePicture(data.imageUrl);
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify({ ...stored, profilePicture: data.imageUrl }));
        showMsg('success', 'Profile picture updated!');
      } else {
        showMsg('error', data.message || 'Upload failed');
      }
    } catch (err) {
      showMsg('error', 'Upload failed. Please try again.');
    } finally {
      setUploadingPic(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/profile`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(profileData),
      });
      const data = await res.json();
      if (res.ok) {
        const stored = JSON.parse(localStorage.getItem('user') || '{}');
        localStorage.setItem('user', JSON.stringify({ ...stored, name: profileData.name, email: profileData.email }));
        showMsg('success', 'Profile updated successfully!');
      } else {
        showMsg('error', data.message || 'Failed to update profile');
      }
    } catch (err) {
      showMsg('error', 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) { showMsg('error', 'New passwords do not match!'); return; }
    if (passwordData.newPassword.length < 6) { showMsg('error', 'Password must be at least 6 characters!'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/change-password`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ currentPassword: passwordData.currentPassword, newPassword: passwordData.newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        showMsg('success', 'Password changed successfully!');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        showMsg('error', data.message || 'Failed to change password');
      }
    } catch (err) {
      showMsg('error', 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!deletePassword) { showMsg('error', 'Please enter your password to confirm deletion'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/account`, {
        method: 'DELETE',
        headers: authHeaders(),
        body: JSON.stringify({ password: deletePassword }),
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
      } else {
        showMsg('error', data.message || 'Failed to delete account');
      }
    } catch (err) {
      showMsg('error', 'Network error. Please try again.');
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl rounded-3xl shadow-2xl border-2 border-yellow-400 p-12 text-center max-w-lg">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full shadow-lg mb-6">
            <User className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-4">Sign In to Access Profile Settings</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8">Manage your profile, change password, and update account settings</p>
          <button onClick={() => window.location.href = '/auth'}
            className="w-full px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-bold rounded-xl hover:shadow-lg transition-all">
            Sign In / Create Account
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 transition-colors duration-300 py-8 px-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-20 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 -right-20 w-96 h-96 bg-yellow-400/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-4xl mx-auto">
        <button onClick={() => window.location.href = '/dashboard'}
          className="mb-6 flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors">
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">Back to Dashboard</span>
        </button>

        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-8 mb-6">
          <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-2">Profile Settings</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage your account settings and preferences</p>
        </div>

        {message.text && (
          <div className={`mb-6 p-4 rounded-2xl border flex items-center gap-3 ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300'
              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300'
          }`}>
            {message.type === 'success'
              ? <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
              : <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />}
            <p className="font-semibold">{message.text}</p>
          </div>
        )}

        {/* Profile Picture */}
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-8 mb-6">
          <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <Camera className="w-6 h-6 text-yellow-500" /> Profile Picture
          </h2>
          <div className="flex items-center gap-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-2xl overflow-hidden bg-gradient-to-br from-yellow-100 to-yellow-200 dark:from-yellow-900/30 dark:to-yellow-800/30 flex items-center justify-center shadow-lg">
                {profilePicture ? (
                  <img src={profilePicture} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-10 h-10 text-yellow-600" />
                )}
              </div>
              {uploadingPic && (
                <div className="absolute inset-0 bg-black/50 rounded-2xl flex items-center justify-center">
                  <Loader className="w-6 h-6 text-white animate-spin" />
                </div>
              )}
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">Upload a clear photo. Max 5MB, JPG/PNG/WEBP.</p>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleProfilePicUpload} className="hidden" />
              <button type="button" onClick={() => fileInputRef.current.click()} disabled={uploadingPic}
                className="px-6 py-2.5 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all disabled:opacity-60 flex items-center gap-2">
                {uploadingPic ? <><Loader className="w-4 h-4 animate-spin" /> Uploading...</> : <><Camera className="w-4 h-4" /> Change Photo</>}
              </button>
            </div>
          </div>
        </div>

        {/* Profile Info */}
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-8 mb-6">
          <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <User className="w-6 h-6 text-yellow-500" /> Profile Information
          </h2>

          {profileLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <form onSubmit={handleUpdateProfile} className="space-y-5">
              {[
                { label: 'Full Name', name: 'name', type: 'text', icon: <User className="w-5 h-5 text-gray-400" />, placeholder: 'John Doe', required: true },
                { label: 'Email Address', name: 'email', type: 'email', icon: <Mail className="w-5 h-5 text-gray-400" />, placeholder: 'you@example.com', required: true },
                { label: 'Phone Number', name: 'phone', type: 'tel', icon: <Phone className="w-5 h-5 text-gray-400" />, placeholder: '+94 77 123 4567', required: false },
                { label: 'Location', name: 'location', type: 'text', icon: <MapPin className="w-5 h-5 text-gray-400" />, placeholder: 'Colombo, Sri Lanka', required: false },
              ].map((field) => (
                <div key={field.name}>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{field.label}</label>
                  <div className="relative">
                    <div className="absolute top-3.5 left-3">{field.icon}</div>
                    <input
                      type={field.type}
                      name={field.name}
                      value={profileData[field.name]}
                      onChange={e => setProfileData({ ...profileData, [e.target.name]: e.target.value })}
                      placeholder={field.placeholder}
                      required={field.required}
                      className="w-full pl-12 pr-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none transition-all text-gray-900 dark:text-gray-100 placeholder-gray-400"
                    />
                  </div>
                </div>
              ))}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Bio</label>
                <textarea
                  name="bio"
                  value={profileData.bio}
                  onChange={e => setProfileData({ ...profileData, bio: e.target.value })}
                  placeholder="Tell us about yourself..."
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none transition-all text-gray-900 dark:text-gray-100 placeholder-gray-400 resize-none"
                />
              </div>
              <button type="submit" disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-bold rounded-xl hover:shadow-xl hover:shadow-yellow-400/30 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-60">
                {loading ? <Loader className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </form>
          )}
        </div>

        {/* Change Password */}
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-xl border border-gray-200/50 dark:border-gray-700/50 p-8 mb-6">
          <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <Lock className="w-6 h-6 text-yellow-500" /> Change Password
          </h2>
          <form onSubmit={handleChangePassword} className="space-y-5">
            {[
              { label: 'Current Password', name: 'currentPassword', show: showCurrentPassword, toggle: () => setShowCurrentPassword(!showCurrentPassword), placeholder: 'Enter current password' },
              { label: 'New Password', name: 'newPassword', show: showNewPassword, toggle: () => setShowNewPassword(!showNewPassword), placeholder: 'Min. 6 characters' },
              { label: 'Confirm New Password', name: 'confirmPassword', show: showConfirmPassword, toggle: () => setShowConfirmPassword(!showConfirmPassword), placeholder: 'Repeat new password' },
            ].map((field) => (
              <div key={field.name}>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">{field.label}</label>
                <div className="relative">
                  <Lock className="absolute top-3.5 left-3 w-5 h-5 text-gray-400" />
                  <input
                    type={field.show ? 'text' : 'password'}
                    name={field.name}
                    value={passwordData[field.name]}
                    onChange={e => setPasswordData({ ...passwordData, [e.target.name]: e.target.value })}
                    placeholder={field.placeholder}
                    required
                    className="w-full pl-12 pr-12 py-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent outline-none transition-all text-gray-900 dark:text-gray-100 placeholder-gray-400"
                  />
                  <button type="button" onClick={field.toggle} className="absolute top-3.5 right-3">
                    {field.show ? <EyeOff className="w-5 h-5 text-gray-400 hover:text-gray-600" /> : <Eye className="w-5 h-5 text-gray-400 hover:text-gray-600" />}
                  </button>
                </div>
              </div>
            ))}
            <button type="submit" disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-white font-bold rounded-xl hover:shadow-xl hover:shadow-yellow-400/30 transition-all transform hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? <Loader className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
              {loading ? 'Changing...' : 'Change Password'}
            </button>
          </form>
        </div>

        {/* Delete Account */}
        <div className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-xl border border-red-200 dark:border-red-800/50 p-8">
          <h2 className="text-2xl font-black text-red-600 dark:text-red-400 mb-4 flex items-center gap-2">
            <Trash2 className="w-6 h-6" /> Danger Zone
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Once you delete your account, there is no going back. This will permanently delete all your data, services, bookings, and reviews.
          </p>
          {!showDeleteConfirm ? (
            <button onClick={() => setShowDeleteConfirm(true)}
              className="px-6 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-all">
              Delete My Account
            </button>
          ) : (
            <div className="space-y-4">
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                <p className="font-bold text-red-800 dark:text-red-300 mb-2">Are you absolutely sure?</p>
                <p className="text-sm text-red-700 dark:text-red-400 mb-4">This action cannot be undone. Enter your password to confirm.</p>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={e => setDeletePassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 rounded-xl focus:ring-2 focus:ring-red-400 outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400"
                />
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setShowDeleteConfirm(false); setDeletePassword(''); }}
                  className="flex-1 px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white font-bold rounded-xl hover:bg-gray-300 transition-all">
                  Cancel
                </button>
                <button onClick={handleDeleteAccount} disabled={loading}
                  className="flex-1 px-6 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition-all disabled:opacity-60">
                  {loading ? 'Deleting...' : 'Yes, Delete My Account'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}